import type { AlertRuleSpec, AlertRuleView } from "@screener/protocol";
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import type { ApiResult } from "./api-result";
import { createRule, listRules, removeRule, replaceRule } from "./rules-api";

/**
 * Контроллер списка правил экрана «Алерты» (tasks.md 6.4), тем же приёмом,
 * что `createAlertController` в `alerts/use-alert-actions.ts`: логика без
 * React, с внедрёнными функциями `rules-api`, тестируемая без рендера. Хук
 * `useAlertRules` ниже — тонкая обёртка, которая держит контроллер в
 * `useRef` и подписывается на его снимок через `useSyncExternalStore`.
 */

export type AlertRulesState =
  | { status: "loading" }
  | { status: "unavailable" }
  | { status: "ready"; rules: AlertRuleView[] };

/**
 * Исход записи (заведение, правка, удаление) — отдельный от `ApiResult`:
 * `busy` не приходит от демона, а значит «запись уже идёт» (design.md
 * решение, спека `web-alerts-screen` «Сохранение недоступно, пока
 * предыдущая попытка не получила ответа»).
 */
export type AlertRuleWriteOutcome =
  | { status: "ok" }
  | { status: "invalid" }
  | { status: "not_found" }
  | { status: "unauthorized" }
  | { status: "unavailable" }
  | { status: "busy" };

export interface AlertRulesControllerDeps {
  listRules: () => Promise<ApiResult<AlertRuleView[]>>;
  createRule: (spec: AlertRuleSpec) => Promise<ApiResult<AlertRuleView>>;
  replaceRule: (id: string, spec: AlertRuleSpec) => Promise<ApiResult<AlertRuleView>>;
  removeRule: (id: string) => Promise<ApiResult<null>>;
  onSessionInvalid: () => void;
  /** Перечитывание карты действий вкладки §8 после успешной записи (design.md решение 7). */
  onRulesChanged: () => void;
}

export interface AlertRulesController {
  /** Заводит контроллер и читает список один раз. */
  start(): void;
  /** Сбрасывает состояние в `loading`; ответы чтений, начатых до вызова, игнорируются. */
  stop(): void;
  /** Перечитывает список; чтение, уже идущее, отдаёт всем ждущим один и тот же результат. */
  reload(): Promise<void>;
  create(spec: AlertRuleSpec): Promise<AlertRuleWriteOutcome>;
  replace(id: string, spec: AlertRuleSpec): Promise<AlertRuleWriteOutcome>;
  remove(id: string): Promise<AlertRuleWriteOutcome>;
  subscribe(listener: () => void): () => void;
  getSnapshot(): AlertRulesState;
  /**
   * Снимок «где-то на экране уже идёт запись» (ревью stage6-alert-web,
   * пункт A.2) — отдельный от `getSnapshot()`, но на той же подписке
   * `subscribe`: `useSyncExternalStore` можно вызвать дважды с одним
   * `subscribe` и разными геттерами снимка.
   */
  getWritingSnapshot(): boolean;
}

export function createAlertRulesController(deps: AlertRulesControllerDeps): AlertRulesController {
  let state: AlertRulesState = { status: "loading" };
  // Поколение растёт на каждый `stop()`: ответ чтения, начатого до него,
  // сверяет своё поколение с текущим и молча отбрасывается — тот же приём,
  // что у `createAlertController` в `alerts/use-alert-actions.ts`.
  let generation = 0;
  let inFlight: Promise<void> | null = null;
  let writing = false;

  const listeners = new Set<() => void>();

  function notifyListeners(): void {
    for (const listener of listeners) {
      listener();
    }
  }

  function setState(next: AlertRulesState): void {
    state = next;
    notifyListeners();
  }

  function setWriting(next: boolean): void {
    if (writing === next) {
      return;
    }
    writing = next;
    notifyListeners();
  }

  function doReload(): Promise<void> {
    if (inFlight) {
      return inFlight;
    }
    const myGeneration = generation;
    const promise = deps
      .listRules()
      .then((result) => {
        if (myGeneration !== generation) {
          // `stop()` состоялся, пока ответ был в пути — не наш больше.
          return;
        }
        if (result.status === "ok") {
          setState({ status: "ready", rules: result.value });
          return;
        }
        if (result.status === "unauthorized") {
          deps.onSessionInvalid();
          return;
        }
        setState({ status: "unavailable" });
      })
      .finally(() => {
        if (inFlight === promise) {
          inFlight = null;
        }
      });
    inFlight = promise;
    return promise;
  }

  /**
   * Перечитывание после записи (ревью, пункт A.4): не подцепляется к
   * чтению, начатому ДО записи — дожидается его и запускает новое, свежее.
   * `expectedGeneration` — поколение на входе в `write()`: если за время
   * ожидания идущего чтения случился `stop()`, новое чтение не запускается
   * (ревью, второй круг, пункт 6) — экран, начавший запись, этого чтения уже
   * не ждёт.
   */
  async function freshReload(expectedGeneration: number): Promise<void> {
    if (inFlight) {
      await inFlight;
    }
    if (expectedGeneration !== generation) {
      return;
    }
    await doReload();
  }

  function start(): void {
    void doReload();
  }

  function stop(): void {
    generation += 1;
    inFlight = null;
    setWriting(false);
    setState({ status: "loading" });
  }

  function reload(): Promise<void> {
    return doReload();
  }

  async function write(
    op: () => Promise<ApiResult<AlertRuleView> | ApiResult<null>>,
  ): Promise<AlertRuleWriteOutcome> {
    if (writing) {
      return { status: "busy" };
    }
    // Поколение на входе — если `stop()` случится, пока запись в пути, ни
    // перечитывание, ни колбэки `onSessionInvalid`/`onRulesChanged` не наши
    // больше (ревью, второй круг, пункт 6): экран, начавший запись, это
    // чтение уже не ждёт.
    const myGeneration = generation;
    setWriting(true);
    try {
      const result = await op();
      const stale = myGeneration !== generation;
      if (result.status === "unauthorized") {
        if (!stale) {
          deps.onSessionInvalid();
        }
        return { status: "unauthorized" };
      }
      if (result.status === "ok") {
        if (!stale) {
          await freshReload(myGeneration);
          if (myGeneration === generation) {
            deps.onRulesChanged();
          }
        }
        return { status: "ok" };
      }
      if (result.status === "not_found") {
        // Правило уже удалено в другой вкладке (спека `web-alerts-screen`):
        // список перечитывается, но это не успешная запись этой вкладки —
        // `onRulesChanged()` здесь не зовём.
        if (!stale) {
          await freshReload(myGeneration);
        }
        return { status: "not_found" };
      }
      return { status: result.status };
    } catch {
      return { status: "unavailable" };
    } finally {
      setWriting(false);
    }
  }

  function create(spec: AlertRuleSpec): Promise<AlertRuleWriteOutcome> {
    return write(() => deps.createRule(spec));
  }

  function replace(id: string, spec: AlertRuleSpec): Promise<AlertRuleWriteOutcome> {
    return write(() => deps.replaceRule(id, spec));
  }

  function remove(id: string): Promise<AlertRuleWriteOutcome> {
    return write(() => deps.removeRule(id));
  }

  function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  function getSnapshot(): AlertRulesState {
    return state;
  }

  function getWritingSnapshot(): boolean {
    return writing;
  }

  return {
    start,
    stop,
    reload,
    create,
    replace,
    remove,
    subscribe,
    getSnapshot,
    getWritingSnapshot,
  };
}

interface UseAlertRulesOptions {
  /** Правила читаются, только пока в клиенте есть действительная сессия. */
  enabled: boolean;
  /**
   * Экран «Алерты» виден (не `hidden`). Переход `false → true` перечитывает
   * список — правка из другой вкладки видна при возврате (design.md решение 2).
   */
  active: boolean;
  onSessionInvalid: () => void;
  onRulesChanged: () => void;
}

export interface UseAlertRulesResult {
  state: AlertRulesState;
  /** Где-то на экране уже идёт запись (ревью stage6-alert-web, пункт A.2) — блокирует «Сохранить»/«Удалить». */
  writing: boolean;
  reload: () => void;
  create: (spec: AlertRuleSpec) => Promise<AlertRuleWriteOutcome>;
  replace: (id: string, spec: AlertRuleSpec) => Promise<AlertRuleWriteOutcome>;
  remove: (id: string) => Promise<AlertRuleWriteOutcome>;
}

export function useAlertRules({
  enabled,
  active,
  onSessionInvalid,
  onRulesChanged,
}: UseAlertRulesOptions): UseAlertRulesResult {
  const onSessionInvalidRef = useRef(onSessionInvalid);
  useEffect(() => {
    onSessionInvalidRef.current = onSessionInvalid;
  }, [onSessionInvalid]);

  const onRulesChangedRef = useRef(onRulesChanged);
  useEffect(() => {
    onRulesChangedRef.current = onRulesChanged;
  }, [onRulesChanged]);

  const controllerRef = useRef<AlertRulesController | undefined>(undefined);
  if (!controllerRef.current) {
    controllerRef.current = createAlertRulesController({
      listRules,
      createRule,
      replaceRule,
      removeRule,
      onSessionInvalid: () => onSessionInvalidRef.current(),
      onRulesChanged: () => onRulesChangedRef.current(),
    });
  }
  const controller = controllerRef.current;

  useEffect(() => {
    if (enabled) {
      controller.start();
      return () => controller.stop();
    }
    controller.stop();
    return undefined;
  }, [enabled, controller]);

  // Экран скрыт атрибутом `hidden`, но смонтирован — переход `false → true`
  // перечитывает список, чтобы правка из другой вкладки была видна при
  // возврате (design.md решение 2).
  const wasActiveRef = useRef(active);
  useEffect(() => {
    const wasActive = wasActiveRef.current;
    wasActiveRef.current = active;
    if (enabled && active && !wasActive) {
      void controller.reload();
    }
  }, [enabled, active, controller]);

  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );
  const writing = useSyncExternalStore(
    controller.subscribe,
    controller.getWritingSnapshot,
    controller.getWritingSnapshot,
  );

  const reload = useCallback(() => {
    void controller.reload();
  }, [controller]);

  const create = useCallback((spec: AlertRuleSpec) => controller.create(spec), [controller]);

  const replace = useCallback(
    (id: string, spec: AlertRuleSpec) => controller.replace(id, spec),
    [controller],
  );

  const remove = useCallback((id: string) => controller.remove(id), [controller]);

  return { state, writing, reload, create, replace, remove };
}
